import React from 'react';
import moment from 'moment';
import { getProxyUrl } from '../../utils/image';
import { getArenaBg } from '../../utils/matches';
import { LEAGUES } from '../../constants/leagues';
import { useTeamLogo } from '../../hooks/team';
import { numberFormat } from '../../utils/numberFormat';
import { UserContext } from '../../context/user-context';

function MatchHeaderOngoingFinished({ match, matchConfig }) {
  const {
    homeTeamName,
    visitingTeamName,
    homeTeamAbbrv,
    visitingTeamAbbrv,
    homeTeamScore,
    visitingTeamScore,
    startDate,
    referees,
  } = match;
  const [homeTeamLogo] = useTeamLogo(homeTeamAbbrv, {
    width: 120,
    height: 120,
  });
  const [visitingTeamLogo] = useTeamLogo(visitingTeamAbbrv, {
    width: 120,
    height: 120,
  });
  const arenabg =
    match?.configLeagueName !== LEAGUES.FUTSAL
      ? getProxyUrl(getArenaBg(homeTeamAbbrv), 1400, 788)
      : '/wp-content/themes/sef-leagues/images/match-hero.png';

  const niceDate = moment(startDate).format('ddd D MMM HH:mm');

  const getGoals = (homeTeam) => {
    const mapGoals = (e) => ({
      player: e.playerName,
      minute: e.minuteWithStoppageTime,
      goalType: e.goalType,
      video: e.video,
    });
    const goalEvents = (match?.matchEvents || [])
      .filter((x) => x.type === 'GOAL')
      .reverse(); // the matchEvents have desc order from the api. The value might be 45+1 minutes which makes it more complicated to sort them locally

    if (homeTeam) {
      return goalEvents.filter((x) => x.byHomeTeam).map(mapGoals);
    }

    return goalEvents.filter((x) => !x.byHomeTeam).map(mapGoals);
  };

  const { playVideo } = React.useContext(UserContext);

  const eventClasses = (isHomeTeam) => {
    return {
      item: isHomeTeam
        ? 'justify-content-end justify-content-lg-start'
        : 'justify-content-start justify-content-lg-end',
      goals: isHomeTeam ? 'order-2 order-lg-0' : 'order-0 order-lg-2',
      scorer: isHomeTeam
        ? 'order-0 order-lg-2 text-end'
        : 'order-2 order-lg-0 text-start',
    };
  };

  const renderGoals = (homeTeam) => {
    const goals = getGoals(homeTeam);
    const classes = eventClasses(homeTeam);

    return goals.map((g) => (
      <li style={{ display: 'flex' }} className={classes.item}>
        <div
          className={`match-events__item ${
            g.video ? 'match-events__item-clickable' : ''
          }`}
          onClick={() => {
            if (g.video) {
              playVideo(g.video);
            }
          }}
          aria-hidden="true"
        >
          <span className={`match-events__minute text-center ${classes.goals}`}>
            {g?.minute}
          </span>

          {g.video ? (
            <div
              className="order-1 text-start"
              style={{ width: '18px !important', height: '18px' }}
            >
              <svg
                width="18"
                height="18"
                viewBox="0 0 18 18"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <g clipPath="url(#clip0_4002_62)">
                  <path
                    d="M8.77393 17.5C7.09279 17.5 5.44941 17.0015 4.05159 16.0675C2.65378 15.1335 1.56432 13.806 0.920974 12.2528C0.277628 10.6996 0.109273 8.99058 0.437248 7.34174C0.765223 5.69291 1.57477 4.17835 2.7635 2.9896C3.95225 1.80085 5.46679 0.991305 7.11565 0.66333C8.76448 0.335355 10.4735 0.503681 12.0267 1.14702C13.5799 1.79037 14.9074 2.87983 15.8414 4.27764C16.7754 5.67546 17.2739 7.31887 17.2739 9C17.2714 11.2536 16.375 13.4141 14.7815 15.0076C13.188 16.6011 11.0275 17.4975 8.77393 17.5ZM8.77393 1.4757C7.28576 1.4757 5.83101 1.917 4.59365 2.74379C3.35629 3.57055 2.39186 4.74568 1.82237 6.12058C1.25287 7.49545 1.10391 9.00836 1.39424 10.4679C1.68456 11.9275 2.40118 13.2682 3.45347 14.3205C4.50577 15.3728 5.84646 16.0894 7.30602 16.3797C8.76559 16.67 10.2785 16.5211 11.6533 15.9516C13.0282 15.3821 14.2034 14.4176 15.0301 13.1803C15.8569 11.9429 16.2982 10.4882 16.2982 9C16.2976 7.00408 15.5049 5.08998 14.0941 3.6781C12.6833 2.2662 10.7698 1.47195 8.77393 1.46973V1.4757Z"
                    fill="#FFD600"
                  />
                  <path
                    d="M11.9162 10.6019C11.8766 10.554 11.8346 10.5073 11.7962 10.4606C11.7242 10.3906 11.6571 10.3243 11.5827 10.2592C11.5323 10.2126 11.4818 10.1696 11.429 10.129L11.3499 10.0689C11.2299 9.97427 11.1026 9.88832 10.9814 9.81095L10.9262 9.77655C10.6802 9.6218 10.4631 9.50757 10.3371 9.4437L10.2038 9.37861L9.91343 9.23611L6.41542 11.1497C5.44939 11.7085 5.64142 13.2303 5.98222 14.1945L12.0795 10.8279C12.032 10.7476 11.9773 10.6719 11.9162 10.6019Z"
                    fill="url(#paint0_linear_4002_62)"
                  />
                  <path
                    d="M13.26 8.86872C13.26 8.7764 13.2516 8.68411 13.2409 8.59179C13.23 8.49888 13.2132 8.40677 13.1906 8.31603C13.1241 8.01747 12.9938 7.73685 12.8087 7.49354C12.7811 7.45753 12.7515 7.42313 12.7201 7.39043C12.6789 7.3472 12.6345 7.30713 12.5873 7.27053L12.5286 7.22256L12.4448 7.16502L12.3741 7.12785L12.3227 7.10027L6.21759 3.80556C6.15143 3.99088 6.09587 4.17982 6.05118 4.37147C5.84649 5.26227 5.86323 6.33296 6.64972 6.77777L10.6396 8.91309L10.7593 8.97785C10.879 9.04259 11.073 9.15649 11.2956 9.30756L11.3471 9.34234C11.456 9.41787 11.5698 9.50178 11.6811 9.5929L11.7517 9.65164C11.7997 9.68909 11.8457 9.72914 11.8894 9.77154C11.9576 9.83387 12.0174 9.89144 12.0809 9.96458C12.1204 10.009 12.1587 10.0533 12.1922 10.0988C12.2437 10.1636 12.289 10.233 12.3275 10.3063C12.3883 10.4126 12.4258 10.5308 12.4376 10.6528C12.5007 10.6125 12.5607 10.5676 12.6172 10.5185C12.6507 10.4873 12.6806 10.4526 12.7129 10.419C12.7333 10.3974 12.756 10.377 12.7752 10.3542C12.8528 10.2607 12.9206 10.1595 12.9775 10.0521C13.0014 10.0101 13.0206 9.97176 13.0386 9.93221C13.123 9.7408 13.1825 9.53932 13.2157 9.33273C13.2383 9.20242 13.2508 9.07053 13.2529 8.93828V8.86752L13.26 8.86872Z"
                    fill="url(#paint1_linear_4002_62)"
                  />
                </g>
                <defs>
                  <linearGradient
                    id="paint0_linear_4002_62"
                    x1="9.82316"
                    y1="10.0317"
                    x2="9.99125"
                    y2="9.21052"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="white" stopOpacity="0.45" />
                  </linearGradient>
                  <linearGradient
                    id="paint1_linear_4002_62"
                    x1="9.77569"
                    y1="6.0943"
                    x2="9.17254"
                    y2="8.70387"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stopColor="white" />
                    <stop offset="1" stopColor="#CFCCCC" />
                  </linearGradient>
                  <clipPath id="clip0_4002_62">
                    <rect
                      width="17"
                      height="17"
                      fill="white"
                      transform="translate(0.273926 0.5)"
                    />
                  </clipPath>
                </defs>
              </svg>
            </div>
          ) : null}
          <span className={classes.scorer}>
            {g.goalType === 'own goal' ? 'Självmål' : g.player}
          </span>
        </div>
      </li>
    ));
  };

  const getMatchTime = () => {
    if (matchConfig?.hideMatchTime !== false) {
      return '';
    }

    if (match?.matchMinuteWithStoppageTime === 'FT') {
      return 'SLUT';
    }

    return match?.matchMinuteWithStoppageTime;
  };

  return (
    <div
      className="hero-image__container hero-image__container--match d-flex align-content-start align-items-lg-center"
      style={{
        backgroundImage: `url('${arenabg}')`,
      }}
    >
      <div className="match-hero">
        <div className="match-hero__team match-hero__team--live">
          <img
            className="match-hero__team-logo"
            src={homeTeamLogo}
            alt="arena"
          />
          <div className="match-hero__team-item align-items-center align-items-lg-start">
            <p className="match-hero__team-name heading-h6 heading-lg-h4 text-transform-none mb-2 text-center text-lg-left">
              {homeTeamName}
            </p>
            <ul className="match-events">{renderGoals(true)}</ul>
          </div>
        </div>
        <div className="match-hero__score match-hero__score--live">
          <p className="heading-h3 heading-md-h2">
            {homeTeamScore} - {visitingTeamScore}
          </p>
          <p className="heading-h6 mb-2">{getMatchTime()}</p>
        </div>
        <div className="match-hero__team match-hero__team--live">
          <div className="match-hero__team-item align-items-center align-items-lg-end order-1 order-lg-0">
            <p className="match-hero__team-name heading-h6 heading-lg-h4 text-transform-none mb-2 text-center text-lg-left">
              {visitingTeamName}
            </p>
            <ul className="match-events">{renderGoals(false)}</ul>
          </div>
          <img
            className="match-hero__team-logo order-0 order-lg-1"
            src={visitingTeamLogo}
            alt=""
          />
        </div>
        <div className="match-hero__info d-flex align-items-lg-center gap-3 gap-lg-5 justify-content-center mt-auto">
          <div className="d-flex flex-column flex-lg-row justify-content-start justify-content-lg-center align-items-center gap-2">
            <img
              width="20"
              src="/wp-content/themes/sef-leagues/images/calendar-logo.png"
              alt="Date"
            />
            <p className="text-lg-small text-tiny text-uppercase text-center text-lg-start pt-lg-1">
              {niceDate}
            </p>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-start justify-content-lg-center align-items-center gap-2">
            <img
              width="20"
              src="/wp-content/themes/sef-leagues/images/referee-logo.png"
              alt="Referee"
            />
            <p className="text-lg-small text-tiny text-uppercase text-center text-lg-start pt-lg-1">
              {referees?.length > 0 ? referees.join(', ') : 'EJ FASTSTÄLLT'}
            </p>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-start justify-content-lg-center align-items-center gap-2">
            <img
              width="20"
              src="/wp-content/themes/sef-leagues/images/arena-logo.png"
              alt="Arena"
            />
            <p className="text-lg-small text-tiny text-uppercase text-center text-lg-start pt-lg-1">
              {match?.arenaName}
            </p>
          </div>
          <div className="d-flex flex-column flex-lg-row justify-content-start justify-content-lg-center align-items-center gap-2">
            <img
              width="20"
              src={`/wp-content/themes/sef-leagues/images/${match?.configLeagueName}/icon.png`}
              alt="League"
            />
            <p className="text-lg-small text-tiny text-uppercase text-center text-lg-start pt-lg-1">
              {`OMGÅNG ${match?.round}`}
            </p>
          </div>
          {match?.spectators && (
            <div className="d-flex flex-column flex-lg-row justify-content-start justify-content-lg-center align-items-center gap-2">
              <img
                width="20"
                src="/wp-content/themes/sef-leagues/images/arena-capacity.png"
                alt="Attendance"
              />
              <p className="text-lg-small text-tiny text-uppercase text-center text-lg-start pt-lg-1">
                {numberFormat(match.spectators)}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MatchHeaderOngoingFinished;
